/**
 * Token Manager - Smart JWT token handling to prevent unnecessary refresh calls
 */

class TokenManager {
  constructor() {
    this.isRefreshing = false;
    this.refreshSubscribers = [];
  }

  /**
   * Check if token is expired or will expire soon
   */
  isTokenExpired(token, bufferMinutes = 60) {
    if (!token) return true;

    try {
      // Parse JWT token to get expiry
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      const bufferTime = bufferMinutes * 60; // Convert to seconds
      
      // Return true if token expires within buffer time
      return payload.exp < (currentTime + bufferTime);
    } catch (error) {
      console.error('Error parsing token:', error);
      return true; // Treat invalid tokens as expired
    }
  }

  /**
   * Get token expiry information
   */
  getTokenInfo(token) {
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = payload.exp - currentTime;
      
      return {
        expiresAt: payload.exp,
        currentTime,
        timeUntilExpiry,
        isExpired: timeUntilExpiry <= 0,
        expiresInMinutes: Math.floor(timeUntilExpiry / 60),
        expiresInHours: Math.floor(timeUntilExpiry / 3600)
      };
    } catch (error) {
      console.error('Error parsing token info:', error);
      return null;
    }
  }

  /**
   * Check if we should refresh the token
   */
  shouldRefreshToken() {
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.log('🔑 No token found');
      return false;
    }

    const isExpired = this.isTokenExpired(token, 60); // 1 hour buffer (23h refresh for 24h tokens)
    const tokenInfo = this.getTokenInfo(token);
    
    if (tokenInfo) {
      console.log(`🔑 Token expires in ${tokenInfo.expiresInHours}h ${tokenInfo.expiresInMinutes % 60}m`);
      
      if (isExpired) {
        console.log('🔑 Token needs refresh (expires within 1 hour)');
        return true;
      } else {
        console.log('🔑 Token is still valid, no refresh needed');
        return false;
      }
    }
    
    return true; // Refresh if we can't parse the token
  }

  /**
   * Add subscriber for refresh token results
   */
  addRefreshSubscriber(callback) {
    this.refreshSubscribers.push(callback);
  }

  /**
   * Notify all subscribers of refresh result
   */
  onRefreshComplete(error, token) {
    this.refreshSubscribers.forEach(callback => callback(error, token));
    this.refreshSubscribers = [];
  }

  /**
   * Set refreshing state
   */
  setRefreshingState(isRefreshing) {
    this.isRefreshing = isRefreshing;
  }

  /**
   * Check if currently refreshing
   */
  isCurrentlyRefreshing() {
    return this.isRefreshing;
  }

  /**
   * Log token information for debugging
   */
  logTokenInfo() {
    const token = localStorage.getItem('token');
    const info = this.getTokenInfo(token);
    
    if (info) {
      console.log('🔑 Token Info:', {
        expiresInHours: info.expiresInHours,
        expiresInMinutes: info.expiresInMinutes,
        timeRemaining: `${info.expiresInHours}h ${info.expiresInMinutes % 60}m`,
        isExpired: info.isExpired,
        shouldRefresh: this.shouldRefreshToken()
      });
    } else {
      console.log('🔑 No valid token found');
    }
  }
}

// Export singleton instance
const tokenManager = new TokenManager();
export default tokenManager; 